import { Injectable } from '@angular/core';
import { NavController } from '@ionic/angular';

import { AppcmsService } from 'src/app/services/core/appcms.service';
import { ChooserService } from 'src/app/services/utils/chooser.service';
import { ConfigService } from "src/app/services/core/config.service";
import { EventsService } from 'src/app/services/core/events.service';
import { FoldersService } from 'src/app/services/utils/folders.service';
import { ModalService } from 'src/app/services/core/modal.service';

import { AiModelsPage } from '../../pages/ai/ai-models/ai-models.page';
import { AiShortcodePage } from '../../pages/ai/ai-shortcode/ai-shortcode.page';

@Injectable({
  providedIn: 'root'
})
export class AiToolsService {

  _detailItem: any | null;

  promptFunctionActions: string[] = ['api', 'code'];

  promptTypes: aiPromptType[] = [
    {
      icon: 'person-outline',
      name: 'prompt_type_user',
      uid: 'user',
    },
    {
      icon: 'terminal-outline',
      name: 'prompt_type_system',
      uid: 'system',
    },
    {
      icon: 'sparkles-outline',
      name: 'prompt_type_assistant',
      uid: 'assistant',
    },
    {
      icon: 'code-slash-outline',
      name: 'prompt_type_function',
      uid: 'function',
    }
  ];

  quickData: any;

  searchOptions: searchOptions;

  store: any = {
    industries: [
      {
        name: 'industry_automotive',
        uid: 'automotive',
      },
      {
        name: 'industry_realestate',
        uid: 'real_estate',
      },
    ],
    job_titles: [
      {
        name: 'job_title_accounting',
        uid: 'accounting',
      },
      {
        name: 'job_title_coding',
        uid: 'coding',
      },
      {
        name: 'job_title_copywriting',
        uid: 'copywriting',
      },
      {
        name: 'job_title_data_analysis',
        uid: 'data_analysis',
      },
      {
        name: 'job_title_ecommerce',
        uid: 'ecommerce',
      },
      {
        name: 'job_title_graphic_design',
        uid: 'graphic_design',
      },
      {
        name: 'job_title_management',
        uid: 'management',
      },
      {
        name: 'job_title_marketing',
        uid: 'marketing',
      },
      {
        name: 'job_title_videography',
        uid: 'videography',
      },
    ],
  };

  taskSavePaths: any[];

  tools: aiTool[] = [
    {
      icon: 'speedometer-outline',
      key: 'enhance',
      token: 50,
    },
    {
      icon: 'expand-outline',
      key: 'extend',
      token: 100,
    },
    {
      icon: 'shield-checkmark-outline',
      key: 'factchecking',
      token: 25,
    },
    {
      icon: 'dice-outline',
      key: 'quiz',
      token: 50,
    },
    {
      icon: 'create-outline',
      key: 'rewrite',
      token: 100,
    },
    {
      icon: 'crop-outline',
      key: 'shorten',
      token: 50,
    },
    {
      icon: 'bulb-outline',
      key: 'simplify',
      token: 50,
    },
    {
      icon: 'thumbs-down-outline',
      key: 'gender',
      token: 150,
    },
    {
      icon: 'thumbs-up-outline',
      key: 'ungender',
      token: 50,
    }
  ];

  view: any = {};

  constructor(
    private AppCMS: AppcmsService,
    private chooser: ChooserService,
    private configService: ConfigService,
    private events: EventsService,
    private folders: FoldersService,
    private modalService: ModalService,
    private navCtrl: NavController,
  ) {

  }

  addIdea(idea: any) {
    idea = JSON.parse(JSON.stringify(idea));

    delete idea.inputs;
    delete idea.loading;
    delete idea.loop;
    delete idea.promptType;
    delete idea.save_path;
    delete idea.save_paths;
    delete idea.try;
    delete idea.type;

    return this.AppCMS.loadPluginData('pipeline', {
      idea: (idea as aiIdea),
    }, ['ai', 'ideas', 'create']);
  }

  addPlan(plan: any) {
    return new Promise((resolve, reject) => {
      console.log('add plan', plan);
    });
  }

  addTask(task: aiTask) {
    return this.AppCMS.loadPluginData('pipeline', {
      task: task,
    }, ['ai', 'tasks', 'create']);
  }

  addToSearchHistory(search: any) {
    return this.AppCMS.loadPluginData('pipeline', {
      search: search,
    }, ['ai', 'search_history', 'create']);
  }

  caption(options: any = {}, blForceRefresh: boolean = false) {
    return this.AppCMS.loadPluginData('pipeline', options, ['ai', 'caption'], {}, blForceRefresh);
  }

  chooseModel(options: any = {}, blForceRefresh: boolean = false) {
    return new Promise(async (resolve, reject) => {

      const models: any = await this.getAvailableModels({
        filter: (options.filter || {}),
      }, blForceRefresh);

      const chooseConfig: chooseConfig = Object.assign(options, {
        data: (models || []).sort((a: any, b: any) => {
          if (a.name < b.name) return -1;
          if (a.name > b.name) return 1;
          return 0;
        }),
        labelKey: 'name',
        multiple: !!options.multiple,
        subLabelKey: 'provider',
        title: 'choose_ai_model',
        valueKey: 'uid',
      });

      const choose: chooseResponse = await this.chooser.choose(chooseConfig, AiModelsPage);
      
      if (choose && choose.data && choose.data.items && choose.data.items.length && (choose.data.items.length > 1)) {
        resolve(choose.data);
      } else
        if (choose && choose.data && choose.data.item) {
          resolve(choose.data.item);
        } else {
          resolve(false);
        }
    });
  }

  chooseProvider(options: any = {}) {
    return new Promise(async (resolve, reject) => {

      const choose: chooseResponse = await this.chooser.choose({
        data: await this.getAvailableProviders(),
        labelKey: 'name',
        title: 'choose_ai_provider',
        valueKey: 'uid',
      });

      if (choose && choose.data && choose.data.item && choose.data.item.uid) {
        resolve(choose.data.item.uid);
      } else {
        resolve(false);
      }
    });
  }

  createContentIdeas(config: any = {}, blForceRefresh: boolean = false) {
    return this.AppCMS.loadPluginData('pipeline', { config: config }, ['ai', 'createContentIdeas'], {}, blForceRefresh);
  }

  createFolder(folder: folder) {
    folder.location = folder.location || 'ai';
    return this.folders.create(folder);
  }

  createImagesSearchQuery(input: string, blForceRefresh: boolean = false) {
    return this.AppCMS.loadPluginData('pipeline', {
      item: {
        post_content: input,
      },
    }, ['ai', 'createImagesSearchQuery'], {}, blForceRefresh);
  }

  createImageVariations(src: string, blForceRefresh: boolean = false) {
    return this.AppCMS.loadPluginData('pipeline', {
      url: src,
    }, ['ai', 'createImageVariations'], {}, blForceRefresh);
  }

  createPlan(plan: aiPlan) {
    return this.AppCMS.loadPluginData('pipeline', {
      plan: plan,
    }, ['ai', 'plans', 'create']);
  }

  createShortcode(data: any = null) {
    data = data || {};

    return new Promise(async (resolve, reject) => {

      const modal: any = await this.modalService.create({
        component: AiShortcodePage,
        componentProps: {
          ai: this,
        },
        animated: true,
        presentingElement: await this.modalService.getTop(),
        cssClass: "defaultModal",
      });

      modal.onWillDismiss().then((response: any) => {
        resolve(response);
      });

      await this.modalService.present(modal);
    });
  }

  deepLoadIdea(idea: aiIdea, options: any = {}) {
    return this.AppCMS.loadPluginData('pipeline', Object.assign(options, {
      idea: idea,
    }), ['ai', 'deepLoadIdea']);
  }

  deleteAssistant(assistant: aiAssistant) {
    return this.AppCMS.loadPluginData('pipeline', {
      assistant_uid: assistant.uid,
    }, ['ai', 'assistants', 'delete']);
  }

  deleteIdea(idea: aiIdea) {
    return this.AppCMS.loadPluginData('pipeline', {
      idea_uid: idea.uid,
    }, ['ai', 'ideas', 'delete']);
  }

  deleteModel(model: aiModel) {
    return this.AppCMS.loadPluginData('pipeline', {
      model_uid: model.uid,
    }, ['ai', 'models', 'delete']);
  }

  deletePlan(plan: aiPlan, options: any = {}) {
    return this.AppCMS.loadPluginData('pipeline', Object.assign(options, {
      plan: plan.uid,
    }), ['ai', 'plans', 'delete']);
  }

  deleteSearchHistoryItem(item: searchOptions) {
    return this.AppCMS.loadPluginData('pipeline', {
      item_uid: item.uid,
    }, ['ai', 'search_history', 'delete']);
  }

  deleteShortcode(shortcode: aiShortcode) {
    return this.AppCMS.loadPluginData('pipeline', {
      shortcode_uid: shortcode.uid,
    }, ['ai', 'shortcodes', 'delete']);
  }

  deleteTask(task: aiTask) {
    return this.AppCMS.loadPluginData('pipeline', {
      task_uid: task.uid,
    }, ['ai', 'tasks', 'delete']);
  }

  detailItem(item: any | null = null) {

    if (item !== null) {
      this._detailItem = item;
      return this;
    }

    return this._detailItem;
  }

  duplicateShortcode(shortcode: aiShortcode) {
    return this.AppCMS.loadPluginData('pipeline', {
      shortcode_uid: shortcode.uid,
    }, ['ai', 'shortcodes', 'duplicate']);
  }

  duplicateTask(task: aiTask) {
    return this.AppCMS.loadPluginData('pipeline', {
      task_uid: task.uid,
    }, ['ai', 'tasks', 'duplicate']);
  }

  async executeFunctionCall(response: any) {
    return new Promise((resolve, reject) => {
      let decoded: any = null, decodedArgs: any = null;

      if (!!response && !!response.output && !!response.output[0] && (response.output[0] === '{')) {
        decoded = JSON.parse(`${(response.output || '').replace(/(\r\n|\n|\r)/gm, "")}`);
        if (!!decoded && !!decoded.name && !!decoded.arguments) {
          decodedArgs = JSON.parse(`${decoded.arguments}`.replace(/(\r\n|\n|\r)/gm, ""));
        }
      }

      if (!decodedArgs) {
        reject('error_missing_arguments');
        return false;
      }

      const call: any = {
        arguments: decodedArgs,
        raw: response,
        name: decoded.name,
      };

      this.events.publish('ai:function:call', call);
      resolve(call);
    });
  }

  editShortcode(shortcode: aiShortcode) {
    return new Promise(async (resolve, reject) => {

      const modal: any = await this.modalService.create({
        component: AiShortcodePage,
        componentProps: {
          ai: this,
          shortcode: shortcode,
        },
        animated: true,
        presentingElement: await this.modalService.getTop(),
        cssClass: "defaultModal",
      });

      modal.onWillDismiss().then((response: any) => {
        resolve(response);
      });

      await this.modalService.present(modal);
    });
  }

  editTaskCronSettings(task: aiTask) {
    this.setQuickData(task);
    this.navCtrl.navigateForward('/ai/task/cron');
  }

  enhance(item: any) {
    return this.AppCMS.loadPluginData('pipeline', {
      item: item,
    }, ['ai', 'enhance']);
  }

  extend(item: any) {
    return this.AppCMS.loadPluginData('pipeline', {
      item: item,
    }, ['ai', 'extend']);
  }

  factchecking(item: any) {
    return this.AppCMS.loadPluginData('pipeline', {
      item: item,
    }, ['ai', 'factchecking']);
  }

  getAIStoreIndustries(blForceRefresh: boolean = false) {
    return this.store.industries;
  }

  getAIStoreJobTitles(blForceRefresh: boolean = false) {
    return this.store.job_titles;
  }

  getAssistants(options: any = {}, blForceRefresh: boolean = false) {
    return this.AppCMS.loadPluginData('pipeline', options, ['ai', 'assistants'], {}, blForceRefresh);
  }

  getAvailableImageModels(options: any = {}, blForceRefresh: boolean = false) {
    options.filter = options.filter || {};
    options.filter.type = 'image';

    return this.getAvailableModels(options, blForceRefresh);
  }

  getAvailableModels(options: any = {}, blForceRefresh: boolean = false) {
    return this.AppCMS.loadPluginData('pipeline', options, ['ai', 'models'], {}, blForceRefresh);
  }

  getAvailableProviders(options: any = {}) {
    return this.AppCMS.loadPluginData('pipeline', options, ['ai', 'providers']);
  }

  getCustomShortcodes(blForceRefresh: boolean = false, options: any = {}) {
    return this.AppCMS.loadPluginData('pipeline', options, ['ai', 'shortcodes'], {}, blForceRefresh);
  }

  getFolders(options: any = {}, blForceRefresh: boolean = false, params: any = {}) {
    options.location = options.location || 'ai';
    return this.folders.get(options, blForceRefresh, params);
  }

  getIdeas(blForceRefresh: boolean = false, options: any = {}) {
    return this.AppCMS.loadPluginData('pipeline', options, ['ai', 'ideas'], {}, blForceRefresh);
  }

  getPromptFunctionActions() {
    return this.promptFunctionActions;
  }

  getPlans(blForceRefresh: boolean = false, options: any = {}) {
    return this.AppCMS.loadPluginData('pipeline', options, ['ai', 'plans'], {}, blForceRefresh);
  }

  getPromptTypes() {
    return this.promptTypes;
  }

  getQuickData() {

    if (!this.quickData) {
      this.quickData = {};
    }

    return this.quickData;
  }

  getSearchHistory(blForceRefresh: boolean = false, options: any = {}) {
    return this.AppCMS.loadPluginData('pipeline', options, ['ai', 'search_history'], {}, blForceRefresh);
  }

  getSearchOptions() {
    return this.searchOptions;
  }

  getStyles(blForceRefresh: boolean = false) {
    return this.AppCMS.loadPluginData('pipeline', {}, ['ai', 'styles'], {}, blForceRefresh);
  }

  getTaskByUid(taskId: number, blForceRefresh: boolean = false) {
    return this.AppCMS.loadPluginData('pipeline', {}, ['ai', 'tasks', taskId], {}, blForceRefresh);
  }

  getTasks(blForceRefresh: boolean = false, options: any = {}) {
    return this.AppCMS.loadPluginData('pipeline', options, ['ai', 'tasks'], {}, blForceRefresh);
  }

  getTaskSavePaths() {
    if (!this.taskSavePaths) {

      const config = this.configService.getConfig();

      let taskSavePaths = [
        {
          icon: 'hand-left-outline',
          name: 'save_none',
          description: 'ai_task_save_path_save_none_description',
          type: 'save_none',
          uid: 'save_none',
        },
        {
          icon: 'calendar-outline',
          name: 'ai_plan',
          description: 'ai_task_save_path_ai_plan_description',
          type: 'ai_plan',
          uid: 'ai_plan',
        },
      ];

      if (config.useWhitelabelAppsExtension) {
        taskSavePaths.push(
          {
            icon: 'phone-portrait-outline',
            name: 'app',
            description: 'ai_task_save_path_app_description',
            type: 'app',
            uid: 'app',
          });
      }

      if (config.useMediaExtension) {
        taskSavePaths.push({
          icon: 'image-outline',
          name: 'image',
          description: 'ai_task_save_path_image_description',
          type: 'image',
          uid: 'image',
        });
      }

      if (config.useNewsletterExtension) {
        taskSavePaths.push({
          icon: 'mail-outline',
          name: 'newsletter',
          description: 'ai_task_save_path_newsletter_description',
          type: 'newsletter',
          uid: 'newsletter',
        });
      }

      if (config.usePostsAdmin) {
        taskSavePaths.push({
          icon: 'create-outline',
          name: 'post',
          description: 'ai_task_save_path_post_description',
          type: 'post',
          uid: 'post',
        });
      }

      if (config.useQuizExtension) {
        taskSavePaths.push({
          icon: 'dice-outline',
          name: 'quiz',
          description: 'ai_task_save_path_quiz_description',
          type: 'quiz',
          uid: 'quiz',
        });
      }

      if (config.useSurveyExtension) {
        taskSavePaths.push({
          icon: 'information-circle-outline',
          name: 'survey',
          description: 'ai_task_save_path_survey_description',
          type: 'survey',
          uid: 'survey',
        });
      }

      if (config.useMediaExtension) {
        taskSavePaths.push({
          icon: 'film-outline',
          name: 'video',
          description: 'ai_task_save_path_video_description',
          type: 'video',
          uid: 'video',
        });
      }

      if (config.useWebsitesExtension) {
        taskSavePaths.push({
          icon: 'desktop-outline',
          name: 'website',
          description: 'ai_task_save_path_website_description',
          type: 'website',
          uid: 'website',
        });
      }

      this.taskSavePaths = taskSavePaths;
    }

    return this.taskSavePaths;
  }

  getTaskSettings(taskId: number, blForceRefresh: boolean = false) {
    return this.AppCMS.loadPluginData('pipeline', {
      task_uid: taskId,
    }, ['ai', 'tasks', 'settings'], {}, blForceRefresh);
  }

  getTools() {
    return this.tools;
  }

  gender(item: any) {
    return this.AppCMS.loadPluginData('pipeline', {
      item: item,
    }, ['ai', 'gender']);
  }

  hasFunctionCallResponse(response: any) {
    let bl: boolean = false, details: any = {};

    try {
      if (!!response && !!response.output && !!response.output[0] && (response.output[0] === '{')) {
        let decoded: any = JSON.parse(`${(response.output || '').replace(/(\r\n|\n|\r)/gm, "")}`);

        if (!!decoded && !!decoded.name && !!decoded.arguments) {
          let decodedArgs: any = JSON.parse(`${decoded.arguments}`.replace(/(\r\n|\n|\r)/gm, ""));

          bl = !!decodedArgs;
          details = decoded;
        }
      }
    } catch (e) {
      console.warn('validating if response is function call failed', e);
    }

    return {
      match: !!bl,
      details: details,
    };
  }

  importShortcode(shortcodeId: number, options: any = {}) {
    return this.AppCMS.loadPluginData("pipeline", Object.assign(options, {
      item: shortcodeId,
    }), ["ai", "store", "importShortcode"]);
  }

  importTask(taskId: number, options: any = {}) {
    return this.AppCMS.loadPluginData("pipeline", Object.assign(options, {
      item: taskId,
    }), ["ai", "store", "importTask"]);
  }

  inspectURL(url: string, blForceRefresh: boolean = false, options: any = {}) {
    return this.AppCMS.loadPluginData('pipeline', Object.assign(options, {
      url: url,
    }), ['ai', 'inspectURL'], {}, blForceRefresh);
  }

  lookupImageByInput(input: string, blForceRefresh: boolean = false) {
    return this.AppCMS.loadPluginData('pipeline', {
      input: input,
    }, ['ai', 'lookupImageByInput'], {}, blForceRefresh);
  }

  lookupImagesOnChildren(children: any[], taskIndex: number) {
    return new Promise((resolve, reject) => {
      if (!!children && !!children.length) {
        children.forEach(async (child: aiTask, childIndex: number) => {
          setTimeout(async () => {
            try {
              const response: any = await this.lookupImageByInput(`${child.name}`);

              if (!!response && !!response.photos) {
                child.media = (response.photos || []).slice(0, 10);
              }

              if (!!response && !!response.photo) {
                child.photo = `${response.photo || response.thumbnail}`;
                child.thumbnail = `${response.thumbnail || response.photo}`;
              }

              this.view.tasks[taskIndex].children[childIndex] = child;

              if (childIndex === (children.length - 1)) {
                resolve(this.view.tasks);
              }
            } catch (e) {
              console.warn('> creating search query for child failed', child, e);

              if (childIndex === (children.length - 1)) {
                resolve(this.view.tasks);
              }
            }
          }, (childIndex * 1000));
        });
      }
    });
  }

  pickTaskSavePath(task: any, options: any = {}) {
    return new Promise(async (resolve, reject) => {
      let paths: any[] = this.getTaskSavePaths();

      if (!!options.exclude && !!options.exclude.length) {
        paths = paths.filter((path: any) => {
          return options.exclude.indexOf(path.uid) === -1;
        });
      }

      const choose: chooseResponse = await this.chooser.choose({
        data: paths,
        labelKey: 'name',
        multiple: !!options.multiple,
        subLabelKey: 'description',
        title: 'choose_target',
        valueKey: 'uid',
      });

      if (choose && choose.data && choose.data.items) {
        resolve(choose.data.items);
      } else
        if (choose && choose.data && choose.data.item && choose.data.item.uid) {
          resolve(choose.data.item.uid);
        } else {
          resolve(false);
        }
    });
  }

  quick(data: any = null) {
    this.setQuickData(data || {});

    const url: string = (!!data && !!data.uid ? `/ai/automation/${data.uid}` : '/ai/quick');

    this.navCtrl.navigateForward(url);
  }
  
  quiz(item: any) {
    return this.AppCMS.loadPluginData('pipeline', {
      item: item,
    }, ['ai', 'quiz']);
  }

  rewrite(item: any) {
    return this.AppCMS.loadPluginData('pipeline', {
      item: item,
    }, ['ai', 'rewrite']);
  }

  search(options: any = {}, params: any = {}, blForceRefresh: boolean = false) {
    return this.AppCMS.loadPluginData('pipeline', Object.assign(params, {
      options: options,
    }), ['ai', 'search'], {}, blForceRefresh);
  }

  setQuickData(data: any) {
    this.quickData = data;
    return this;
  }

  setSearchOptions(options: searchOptions) {
    this.searchOptions = options;
    return this;
  }

  shorten(item: any) {
    return this.AppCMS.loadPluginData('pipeline', {
      item: item,
    }, ['ai', 'shorten']);
  }

  simplify(item: any) {
    return this.AppCMS.loadPluginData('pipeline', {
      item: item,
    }, ['ai', 'simplify']);
  }

  ungender(item: any) {
    return this.AppCMS.loadPluginData('pipeline', {
      item: item,
    }, ['ai', 'ungender']);
  }

  updateIdea(idea: aiIdea) {
    return this.AppCMS.loadPluginData('pipeline', {
      idea: idea,
    }, ['ai', 'ideas', 'update']);
  }

  updatePlan(plan: aiPlan) {
    return this.AppCMS.loadPluginData('pipeline', {
      plan: plan,
    }, ['ai', 'plans', 'update']);
  }

  updateTask(task: aiTask) {
    return this.AppCMS.loadPluginData('pipeline', {
      task: task,
    }, ['ai', 'tasks', 'update']);
  }

  updateTaskSettings(task: aiTask) {
    return this.AppCMS.loadPluginData('pipeline', {
      task: task,
    }, ['ai', 'tasks', 'settings', 'update']);
  }

}